import React, { useContext } from "react";
import {
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Paper,
  Box,
} from "@mui/material";
import useDownloadAttachment from "../../../hooks/useDownloadAttachment";
import AttachmentsContext from "../../../context/attachmentsContext";
import NewDownloadIcon from "../../../components/VectorIcons/NewDownloadIcon";
import NewFileViewIcon from "../../../components/VectorIcons/NewFileViewIcon";

import AppContext from "../../../context/appContext";
import OrderArrow from "../../../components/VectorIcons/OrderArrow";
import {
  tableBodyCellProps,
  tableHeadCellProps,
  tableHeadFormatCellProps,
  tableHeadNameCellProps,
  tableHeadSharedAtCellProps,
  tableHeadVersionCellProps,
} from "./styles";

export function Table({ project, attachments }) {
  const { setFileToView } = useContext(AttachmentsContext);
  const { downloadSharedAttachment } = useDownloadAttachment();
  const appContext = useContext(AppContext);

  const [orderBy, setOrderBy] = React.useState("sharedAt-desc");
  const [showOrderArrow, setShowOrderArrow] = React.useState(false);

  function sortAttachments(attachments, orderBy) {
    if (!orderBy) return attachments;

    const [field, direction] = orderBy.split("-");
    const isAsc = direction === "asc";

    const getValue = (obj, path) =>
      path.split(".").reduce((acc, part) => acc?.[part], obj);

    const fieldMap = {
      name: "name",
      type: "format",
      version: "version",
      sharedBy: "grantedBy.name",
      sharedAt: "grantedAt",
    };

    const key = fieldMap[field];
    if (!key) return attachments;

    const getNameAfterFirstSlash = (value) => {
      if (typeof value !== "string") return value;
      const firstSlashIndex = value.indexOf("/");
      return firstSlashIndex >= 0
        ? value.substring(firstSlashIndex + 1)
        : value;
    };

    const sorted = [...attachments].sort((a, b) => {
      let aValue = getValue(a, key);
      let bValue = getValue(b, key);

      if (field === "name") {
        aValue = getNameAfterFirstSlash(aValue);
        bValue = getNameAfterFirstSlash(bValue);
      }

      if (field === "sharedAt") {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      }

      if (typeof aValue === "string") aValue = aValue.toLowerCase();
      if (typeof bValue === "string") bValue = bValue.toLowerCase();

      if (aValue < bValue) return isAsc ? -1 : 1;
      if (aValue > bValue) return isAsc ? 1 : -1;
      return 0;
    });

    return sorted;
  }

  const sortedAttachments = sortAttachments(attachments, orderBy);

  const TableHeadCell = ({ children, orderA, orderB }) => (
    <Box
      style={{
        display: "flex",
        alignItems: "center",
      }}
      onClick={() => setOrderBy(orderBy === orderA ? orderB : orderA)}
    >
      <span
        style={{
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          position: "relative",
          userSelect: "none",
        }}
      >
        {children}
        {showOrderArrow && (
          <OrderArrow
            upColor={orderBy === orderA ? "#666666" : "#CCCCCC"}
            downColor={orderBy === orderB ? "#666666" : "#CCCCCC"}
            style={{ position: "absolute", left: "100%", marginLeft: "3px" }}
          />
        )}
      </span>
    </Box>
  );

  return (
    <TableContainer component={Paper} elevation={0}>
      <MuiTable
        sx={{
          "& tr:last-child td": {
            borderBottom: "none",
          },
        }}
      >
        <TableHead
          onMouseEnter={() => setShowOrderArrow(true)}
          onMouseLeave={() => setShowOrderArrow(false)}
        >
          <TableRow>
            <TableCell {...tableHeadNameCellProps}>
              <TableHeadCell orderA="name-asc" orderB="name-desc">
                Nome
              </TableHeadCell>
            </TableCell>
            <TableCell {...tableHeadFormatCellProps}>
              <TableHeadCell orderA="type-asc" orderB="type-desc">
                Tipo
              </TableHeadCell>
            </TableCell>
            <TableCell {...tableHeadVersionCellProps}>
              <TableHeadCell orderA="version-asc" orderB="version-desc">
                Versão
              </TableHeadCell>
            </TableCell>
            <TableCell {...tableHeadCellProps}>
              <TableHeadCell orderA="sharedBy-asc" orderB="sharedBy-desc">
                Compartilhado por
              </TableHeadCell>
            </TableCell>
            <TableCell {...tableHeadSharedAtCellProps}>
              <TableHeadCell orderA="sharedAt-asc" orderB="sharedAt-desc">
                Compartilhado em
              </TableHeadCell>
            </TableCell>
            <TableCell
              {...tableHeadCellProps}
              align="center"
              style={{
                userSelect: "none",
              }}
            >
              Visualizar/Baixar
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {sortedAttachments.map((row, index) => {
            const firstSlashIndex =
              typeof row.name === "string" ? row.name.indexOf("/") : -1;
            const displayedName =
              firstSlashIndex >= 0
                ? row.name.substring(firstSlashIndex + 1)
                : row.name;

            const fileToVisualize = {
              sharingId: row.sharingId,
              origin: "visualizador-textos-compartilhados",
              mode: 1,
              autoSave: false,
            };

            const fileToDownload = {
              sharingId: row.sharingId,
              origin: "textos-compartilhados",
              mode: 0,
            };

            return (
              <TableRow
                key={`${index}-${row.id}-${row.name}-${row.version}-${project.projectId}`}
              >
                <TableCell {...tableBodyCellProps}>{displayedName}</TableCell>
                <TableCell {...tableBodyCellProps}>{row.format}</TableCell>
                <TableCell {...tableBodyCellProps} align="center">
                  {row?.version ? `${row?.version}ª` : "-"}
                </TableCell>
                <TableCell {...tableBodyCellProps}>
                  {row.grantedBy?.name || "-"}
                </TableCell>
                <TableCell {...tableBodyCellProps}>
                  {appContext.convertDatePtBr(row.grantedAt)}
                </TableCell>
                <TableCell {...tableBodyCellProps} align="center">
                  <IconButton
                    size="small"
                    onClick={() => setFileToView(fileToVisualize)}
                    sx={{
                      width: 32,
                      height: 32,
                      marginRight: "8px",
                    }}
                    color="primary"
                  >
                    <NewFileViewIcon />
                  </IconButton>

                  <IconButton
                    size="small"
                    onClick={() => downloadSharedAttachment(fileToDownload)}
                    sx={{ width: 32, height: 32 }}
                    color="primary"
                  >
                    <NewDownloadIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </MuiTable>
    </TableContainer>
  );
}
